/**
 * © 2025 Grupo Salamander SAPI de CV
 * Author: Pablo Adoue Peralta
 *
 * Utility functions for UIButton component including click handlers and icon rendering.
 */

import React, { useRef, useCallback } from 'react'
import { UIIcon } from '../UIIcon'
import type { TUIButtonIconValue } from './types'

/**
 * Button action types supported by the button
 */
type TButtonAction = {
  onClick?: () => void
  href?: string
}

const CLICK_COOLDOWN_MS = 400

/**
 * React hook that creates a click handler with double-click protection.
 * Uses useRef to persist the cooldown timestamp across re-renders.
 * Supports onClick callbacks and external links (href opens in new tab).
 *
 * Root cause of SAL-REG-004 / CC-REG-004 (2026-09-25): the cooldown used to
 * key ONLY on wall-clock time, so any two clicks on the same button within
 * 400ms were treated as one accidental double-tap and the second was
 * silently dropped — no handler call, no error. That is correct for a
 * button whose `onClick` always performs the same action, but a caller can
 * legitimately disable the button between two clicks to signal "this next
 * click means something different now" (e.g. a submit button that runs a
 * lookup on click 1 and, once the lookup's async work re-enables it,
 * performs the real submit on click 2 — see `DynamicForm`'s
 * `submitAttempt` rules). A fast lookup response, or an automated/e2e
 * click, easily lands the second click inside the same 400ms window,
 * silently swallowing it. The cooldown now also asks "has this button been
 * disabled since the last accepted click?" — a `disabled` transition is a
 * real state change the caller made on purpose, so the click that follows
 * one is never treated as a bounce, however soon it arrives. A click that
 * repeats while the button stayed continuously enabled the whole time (the
 * genuine double-tap) is still suppressed exactly as before.
 */
export function useClickHandler(
  action: TButtonAction,
  disabled?: boolean
): () => void {
  const lastClickRef = useRef(0)
  const disabledSinceLastClickRef = useRef(false)

  // Recorded at render time (not in an effect) so it reflects the CURRENT
  // `disabled` value by the time a click that arrives before the next
  // render is handled — a plain derived-ref update, no side effect beyond
  // the ref itself.
  if (disabled) disabledSinceLastClickRef.current = true

  return useCallback(() => {
    if (disabled) {
      return
    }

    const now = Date.now()
    const isBounce =
      now - lastClickRef.current < CLICK_COOLDOWN_MS &&
      !disabledSinceLastClickRef.current
    if (isBounce) {
      return
    }
    lastClickRef.current = now
    disabledSinceLastClickRef.current = false

    if (typeof action.onClick === 'function') {
      action.onClick()
    } else if (
      typeof action.href === 'string' &&
      typeof globalThis.window !== 'undefined'
    ) {
      globalThis.window.open(action.href, '_blank')
    }
  }, [action.onClick, action.href, disabled])
}

/**
 * Renders a UIIcon from a TUIButtonIconValue (string or TUIIconProps).
 */
export function renderIcon(
  icon?: TUIButtonIconValue
): React.ReactElement | undefined {
  if (!icon) return undefined
  // Decorative: the button's own mandatory `tooltip` carries the accessible
  // name (plan task 1.7's accessibility discriminant).
  if (typeof icon === 'string') return <UIIcon name={icon} decorative />
  return <UIIcon {...icon} decorative />
}

/**
 * Renders a UIIcon with a size derived from a fontSize string.
 */
export function renderIconWithProps(
  icon?: TUIButtonIconValue,
  props?: { fontSize?: 'small' | 'medium' | 'large' }
): React.ReactElement | null {
  if (!icon) return null
  const sizeMap: Record<string, number> = { small: 20, medium: 24, large: 28 }
  const size = props?.fontSize ? sizeMap[props.fontSize] || 24 : 24
  if (typeof icon === 'string')
    return <UIIcon name={icon} size={size} decorative />
  return <UIIcon {...icon} size={icon.size ?? size} decorative />
}
